'use client';

import { makePath, matchPath } from '@lfg/core/lib';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Collapse, Container, Drawer, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ReactElement, cloneElement, useCallback, useEffect, useMemo, useState } from 'react';

import HexagonalButton from '@/components/bases/HexagonalButton';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpandedItem, setMobileExpandedItem] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const navs = useMemo(
    () => [
      { label: 'News', href: '/news', pattern: makePath('/news') },
      // { label: 'News', href: 'https://medium.com/@LFG_Inc', pattern: makePath('/news'), target: '_blank' },
      { label: 'Learn', href: 'https://support.lfg.inc/', pattern: makePath('/learn'), target: '_blank' },
      { label: 'About Us', href: '/about-us', pattern: makePath('/about-us') },
      {
        label: 'Products',
        href: '/products',
        pattern: makePath('/products'),
        children: [
          { label: 'Core Sale Launchpad', href: 'https://launch.lfg.inc/' },
          { label: 'LFG Orbit', href: 'https://orbit.lfg.inc/' },
          // { label: 'Unstoppable Domains', href: 'https://get.unstoppabledomains.com/lfg/' },
        ],
      },
      {
        label: 'Services',
        href: '/services',
        pattern: makePath('/services'),
        children: [
          { label: 'Advisory Services', href: '/services?service=advisory-services' },
          { label: 'Community Management', href: '/services?service=community-management' },
          { label: 'Customer Support & Education', href: '/services?service=customer-support-education' },
          { label: 'Development Services', href: '/services/development-services' },
          { label: 'Exchange Listing Strategy', href: '/services?service=exchange-listing-strategy' },
          { label: 'Live Streaming Services', href: '/services?service=live-streaming-services' },
          { label: 'Market Making Services', href: '/services?service=market-making-services' },
          { label: 'Marketing Services', href: '/services?service=marketing-services' },
        ],
      },
    ],
    [],
  );

  const handleContactUsClick = useCallback(() => {
    window.location.href = '/contact-us';
  }, []);

  const renderNav = useCallback(
    (_onMobileMenuClose?: () => void) => {
      return navs.map(nav => {
        const isActive = matchPath(pathname, nav.pattern);

        if (nav.children && nav.children.length > 0) {
          const handleCloseMenu = () => setActiveDropdown(null);
          const isOpen = activeDropdown === nav.label;

          return (
            <Box
              key={nav.label}
              position={'relative'}
              onMouseEnter={() => setActiveDropdown(nav.label)}
              onMouseLeave={() => setActiveDropdown(null)}>
              <Link href={nav.href} style={{ textDecoration: 'none' }} target={nav.target}>
                <Typography
                  sx={{
                    color: isActive ? '#FFFFFF' : '#ffffffe2',
                    fontWeight: 400,
                    textTransform: 'uppercase',
                    '&:hover': { color: '#FFFFFF' },
                  }}>
                  {nav.label}
                </Typography>
              </Link>

              {isOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1300,
                    bgcolor: '#060912',
                    minWidth: 220,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    py: 0,
                  }}>
                  {nav.children.map(item => (
                    <Link
                      key={item.label}
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{ textDecoration: 'none', display: 'block' }}
                      onClick={handleCloseMenu}>
                      <Box
                        sx={{
                          minHeight: 40,
                          py: 1.5,
                          px: 2,
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
                        }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            lineHeight: '20px',
                            color: '#FFFFFF',
                          }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Link>
                  ))}
                </Box>
              )}
            </Box>
          );
        }

        return (
          <Link key={nav.label} href={nav.href} style={{ textDecoration: 'none' }} target={nav.target}>
            <Typography
              sx={{
                color: isActive ? '#FFFFFF' : '#ffffffe2',
                fontWeight: 400,
                textTransform: 'uppercase',
                '&:hover': { color: '#FFFFFF' },
              }}>
              {nav.label}
            </Typography>
          </Link>
        );
      });
    },
    [navs, pathname, activeDropdown],
  );

  const renderMobileNav = useCallback(
    () =>
      navs.map(nav => {
        const isActive = matchPath(pathname, nav.pattern);

        if (nav.children && nav.children.length > 0) {
          const isExpanded = mobileExpandedItem === nav.label;
          return (
            <Box key={nav.label}>
              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Link href={nav.href} style={{ textDecoration: 'none', flex: 1 }}>
                  <Typography
                    sx={{
                      color: isActive ? '#FFFFFF' : '#ffffffe2',
                      fontWeight: 400,
                      textTransform: 'uppercase',
                    }}>
                    {nav.label}
                  </Typography>
                </Link>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileExpandedItem(isExpanded ? null : nav.label);
                  }}
                  sx={{ padding: 0.5, color: '#ffffffe2' }}>
                  <KeyboardArrowDownIcon
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </IconButton>
              </Stack>
              <Collapse in={isExpanded}>
                <Stack sx={{ pl: 2, pt: 1 }} gap={0.5}>
                  {nav.children.map(item => (
                    <Link
                      key={item.label}
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{ textDecoration: 'none' }}>
                      <Typography
                        sx={{
                          color: '#ffffffe2',
                          fontSize: 15,
                          py: 0.75,
                          '&:hover': { color: '#FFFFFF' },
                        }}>
                        {item.label}
                      </Typography>
                    </Link>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        }

        return (
          <Link key={nav.label} href={nav.href} style={{ textDecoration: 'none' }} target={nav.target}>
            <Typography
              sx={{
                color: isActive ? '#FFFFFF' : '#ffffffe2',
                fontWeight: 400,
                textTransform: 'uppercase',
                '&:hover': { color: '#FFFFFF' },
              }}>
              {nav.label}
            </Typography>
          </Link>
        );
      }),
    [navs, pathname, mobileExpandedItem],
  );

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        boxShadow: 'none',
        transition: 'all 0.3s ease-in-out',
        py: 1,
        zIndex: theme => theme.zIndex.drawer + 1,
      }}>
      <Container maxWidth='xl'>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Stack alignItems={'center'} justifyContent={'center'}>
            <Link href='/' style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box sx={{ position: 'relative', width: 50, height: 50 }}>
                <Image src='/logo.png' alt='LFG Logo' fill style={{ objectFit: 'contain' }} priority />
              </Box>
            </Link>
          </Stack>

          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <Stack gap={4} direction={'row'} alignItems={'center'} display={{ xs: 'none', md: 'flex' }}>
              {renderNav()}
              <HexagonalButton
                width={120}
                height={38}
                labelClassName='text-xl'
                label={'CONTACT US'}
                onClick={handleContactUsClick}
              />
            </Stack>

            <MobileMenu>
              <Stack gap={2} padding={3}>
                {renderMobileNav()}
                <HexagonalButton
                  width={120}
                  height={38}
                  labelClassName='text-xl'
                  label={'CONTACT US'}
                  onClick={handleContactUsClick}
                />
              </Stack>
            </MobileMenu>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

interface MobileMenuProps {
  children: ReactElement<{ onClick?: () => void }>;
}

// Todo: Recheck
const MobileMenu = ({ children }: MobileMenuProps) => {
  const [initialPadding, setInitialPadding] = useState<string>('');
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>(false);

  const toggle = () => setIsDrawerOpened(prev => !prev);
  const handleClose = () => setIsDrawerOpened(false);

  useEffect(() => {
    if (isDrawerOpened) {
      setInitialPadding(document.body.style.paddingRight);
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.paddingRight = `${scrollBarWidth}px`;

      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.paddingRight = initialPadding;
      };
    }
  }, [isDrawerOpened, initialPadding]);

  return (
    <Stack sx={{ display: { xs: 'contents', md: 'none' } }}>
      <IconButton onClick={toggle} sx={{ padding: 0 }}>
        {isDrawerOpened ? (
          <CloseIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        ) : (
          <MenuIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        )}
      </IconButton>

      <Drawer
        anchor={'top'}
        hideBackdrop
        variant={'temporary'}
        open={isDrawerOpened}
        onClose={() => setIsDrawerOpened(false)}
        sx={{ zIndex: 100 }}
        PaperProps={{
          sx: {
            height: '100dvh',
            paddingTop: '72px',
            backgroundColor: '#060912',
            boxShadow: 'none !important',
          },
        }}>
        {cloneElement(children, {
          onClick: handleClose,
        })}
      </Drawer>
    </Stack>
  );
};

export default Header;
